import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCartShopping } from "@fortawesome/free-solid-svg-icons";

import PaymentGatewayRedirection from "./auxiliar/PaymentGatewayRedirection";
import ShowManagerReferral from "../manager/ShowManagerReferral";
import { fetchData, handleSubmit } from "../../auxiliar/auxFunctions";
import { getClinicPaymentData, getClinicPaymentGatewayParams } from "../../actions/clinics";
import PaymentDetailsSkeleton from "../general/auxiliar/skeleton/PaymentDetailsSkeleton";

const PaymentDetails = () => {
    const dispatch = useDispatch();
    const { id: clinicId } = useSelector((state) => state.activeClinic);

    const [data, setData] = useState();
    const [isLoading, setIsLoading] = useState(true);
    const [paymentData, setPaymentData] = useState({ waiting: true });

    useEffect(() => {
        setIsLoading(true);
        fetchData(dispatch, getClinicPaymentData, clinicId, setData).finally(() => {
            setIsLoading(false);
        });
    }, []);

    const fetchPaymentData = handleSubmit(
        fetchData,
        dispatch,
        getClinicPaymentGatewayParams,
        { clinic_id: clinicId },
        setPaymentData,
    );

    const content = {
        baseText: `Tu suscripción a MiFisio Agenda incluye todas las funcionalidades por solo ${data?.price}€ al mes, sin ningún tipo de cargo adicional ni letra pequeña.`,
        freeMonthsText:
            data?.free_months === 0 ? (
                <>
                    Todavía no has obtenido ningún mes gratis invitando a tus amigos.{" "}
                    <a href="#referral-code" className="link">
                        ¿A qué esperas para compartir MiFisio Agenda con ellos?
                    </a>
                </>
            ) : (
                <>
                    Además, gracias a tus referidos, dispones de {data?.free_months}{" "}
                    {data?.free_months === 1 ? "mes gratuito" : "meses gratuitos"}.{" "}
                    <a href="#referral-code" className="link">
                        ¡Sigue compartiendo y acumula más beneficios!
                    </a>
                </>
            ),
        paymentMethodsText: data?.is_paid ? (
            <>
                El mes actual ya ha sido pagado, pero puedes cambiar el metodo de pago para los
                siguientes meses haciendo click{" "}
                <span className="hover:cursor-pointer" onClick={fetchPaymentData}>
                    aquí <FontAwesomeIcon className="text-secondary" icon={faCartShopping} />
                </span>
                .
            </>
        ) : (
            <>
                El mes actual no ha sido pagado{" "}
                {data?.payment_method_registered
                    ? "y no tienes ningún método de pago registrado. Puedes registrar uno haciendo click"
                    : "El método de pago registrado ha fallado al intentar realizar el pago. Puedes reintentarlo haciendo click"}{" "}
                <span className="hover:cursor-pointer" onClick={fetchPaymentData}>
                    aquí <FontAwesomeIcon className="text-secondary" icon={faCartShopping} />
                </span>
                .
            </>
        ),
    };

    return (
        <section>
            <h2 className="sr-only">Detalles de tu suscripción a MiFisio Agenda</h2>

            {isLoading ? (
                <PaymentDetailsSkeleton />
            ) : (
                <div className="mb-8 grid grid-cols-1 md:grid-cols-6 gap-5">
                    <div className="md:col-span-4 xl:col-span-5">
                        <h3 className="text-primary text-2xl">Plan contratado: {data?.plan}</h3>

                        <p className="lg:text-lg my-3">{content.baseText}</p>

                        <p className="lg:text-lg my-3">{content.freeMonthsText}</p>

                        <p className="lg:text-lg my-3">{content.paymentMethodsText}</p>
                    </div>

                    <div className="md:col-span-2 xl:col-span-1 flex">
                        <div className="stats shadow-sm bg-base-200 text-center align-middle grow">
                            <div className="stat max-w-full">
                                <div className="stat-title text-primary text-xl break-words whitespace-normal">
                                    Precio Final
                                </div>
                                <div className="stat-value break-words whitespace-normal">
                                    {data?.price}€
                                </div>
                                <div className="stat-desc break-words whitespace-normal">
                                    Pago único mensual.
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="bg-base-200 shadow-xl w-full rounded-lg p-8">
                <h3 className="text-secondary text-2xl">¿Cómo funcionan los pagos?</h3>

                <p className="text-base lg:text-lg mt-3">
                    El servicio de pago de MiFisio Agenda se gestiona automáticamente:
                </p>

                <ul className="text-base lg:text-lg mb-3 px-3 list-disc list-inside">
                    <li>
                        Tu suscripción se cobra automáticamente el día 1 de cada mes. Si la cuenta
                        tiene meses gratuitos disponibles no se realiza ningún cobro, si no, se
                        cobra el importe total a la tarjeta guardada.
                    </li>
                    <li>
                        Si el pago inicial falla se puede reintentar manualmente, y si el pago tiene
                        éxito se guardará la tarjeta para futuros pagos.
                    </li>
                    <li>Si el día 7 todavía no se ha pagado se realiza un segundo intento.</li>
                    <li>
                        En caso de que este segundo intento también falle, la cuenta será bloqueada
                        automáticamente hasta que se regularice el pago.
                    </li>
                </ul>

                <p className="text-base lg:text-lg my-3">
                    En cualquier momento puedes actualizar el método de pago y no se cobrará nada si
                    el mes actual ya ha sido pagado. Si los pagos fallan, también puedes
                    reintentarlos manualmente con la misma o con una nueva tarjeta.
                </p>

                <p className="text-base lg:text-lg my-3">
                    Si tienes cualquier duda puedes contactar con nosotros a través de{" "}
                    <a href="mailto:info@mifisioagenda.com" className="link">
                        info@mifisioagenda.com
                    </a>
                    .
                </p>
            </div>

            <div className="my-8" id="referral-code">
                <ShowManagerReferral code={data?.referral_code} />
            </div>

            <PaymentGatewayRedirection paymentData={paymentData} />
        </section>
    );
};

export default PaymentDetails;
